// MatchList.jsx
import { useEffect, useState } from 'react';
import axios from 'axios';
import MatchForm from './MatchForm';
import styles from '../default.module.css';
import { usePermissionGuard } from '../../adminPanelTools';
import { showErrorToast } from '../../../../code/ToastHelper';
export default function MatchList() {
    const checking = usePermissionGuard('handle_matches');
    const [matches, setMatches] = useState([]);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [baseDate, setBaseDate] = useState(new Date()); // Week "cursor"
    const [loading, setLoading] = useState(false);

    const getWeekRange = (date) => {
        const day = date.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        const monday = new Date(date);
        monday.setDate(date.getDate() + diffToMonday);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return { monday, sunday };
    };
    const getWeekNumber = (d) => {
        const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        const dayNum = date.getUTCDay() || 7;
        date.setUTCDate(date.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
        return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    };

    const formatDate = (d) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

    const formatISODate = (d) => d.toISOString().split('T')[0]; // YYYY-MM-DD for API

    // Fetch matches for current 3-week window
    const fetchMatches = async (startDate, endDate) => {
        setLoading(true);
        try {
            const res = await axios.get('/api/matches/get_matches_by_date', {
                params: {
                    start: formatISODate(startDate),
                    end: formatISODate(endDate),
                },
            });
            setMatches(res.data);
        } catch (error) {
            console.error('Failed to fetch matches:', error);
            setMatches([]);
        } finally {
            setLoading(false);
        }
    };

    // const fetchMatches = async () => {
    //     const res = await axios.get('/api/matches/get_all_matches');
    //     setMatches(res.data);
    // };
    const getDateRangeFromBaseDate = (base) => {
        const baseMonday = getWeekRange(base).monday;
        const start = new Date(baseMonday);
        start.setDate(start.getDate() - 7); // one week before

        const end = new Date(baseMonday);
        end.setDate(end.getDate() + 13); // one week after + 6 days

        return { start, end };
    };
    useEffect(() => {
        // Calculate 3-week window: from Monday 1 week before baseDate to Sunday 1 week after baseDate
        const { start, end } = getDateRangeFromBaseDate(baseDate);// one week after + 6 days (total 3 weeks)
        fetchMatches(start, end);
    }, [baseDate, getDateRangeFromBaseDate, fetchMatches]);

    const deleteMatch = async (match) => {
        const dateObj = new Date(match.date);
        const formattedDate = dateObj.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });

        const timeObj = new Date(`1970-01-01T${match.time}`);
        const formattedTime = timeObj.toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
        });
        const confirmDelete = window.confirm(`Möchtest du das Spiel gegen ${match.opponent} am ${formattedDate} um ${formattedTime} wirklich löschen?`);
        if (!confirmDelete) return;

        try {
            await axios.delete(`/api/matches/${match.id}`);
            const { start, end } = getDateRangeFromBaseDate(baseDate);// one week after + 6 days (total 3 weeks)

            fetchMatches(start, end);
        } catch (error) {
            console.error("❌ Fehler beim Löschen des Spiels:", error);
            showErrorToast("Fehler beim Löschen. Bitte versuche es erneut.");
        }
    };
    if (checking) {
        return <div className={styles.container}><p>Lade...</p></div>; // or return null if you want no UI at all
    }
    return (
        <div className={styles.container}>
            <h1>Fußballspiele verwalten</h1>

            <MatchForm match={selectedMatch} onSuccess={() => {
                setSelectedMatch(null);
                const { start, end } = getDateRangeFromBaseDate(baseDate);// one week after + 6 days (total 3 weeks)
                fetchMatches(start, end);
            }} />

            {/* Week Navigation */}
            <div className={styles.weekNav}>
                <button
                    className={styles.greenButton}
                    onClick={() => setBaseDate(new Date(baseDate.setDate(baseDate.getDate() - 21)))}>
                    ← 3 Wochen zurück
                </button>

                <div className={styles.weekInfo}>
                    {(() => {
                        const prevWeek = getWeekRange(new Date(baseDate.getTime() - 7 * 24 * 60 * 60 * 1000));
                        // const currWeek = getWeekRange(baseDate);
                        const nextWeek = getWeekRange(new Date(baseDate.getTime() + 7 * 24 * 60 * 60 * 1000));

                        const startDate = prevWeek.monday;
                        const endDate = nextWeek.sunday;

                        const firstKW = getWeekNumber(startDate);
                        const lastKW = getWeekNumber(nextWeek.monday);

                        return (
                            <span>
                                KW {firstKW} ({formatDate(startDate)} – {formatDate(endDate)}) KW {lastKW}
                            </span>
                        );
                    })()}
                </div>

                <button
                    className={styles.greenButton}
                    onClick={() => setBaseDate(new Date(baseDate.setDate(baseDate.getDate() + 21)))}>
                    3 Wochen vor →
                </button>
            </div>

            {loading ? (
                <p>Lade Spiele...</p>
            ) : (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Team</th>
                            <th>Ort</th>
                            <th>Datum</th>
                            <th>Uhrzeit</th>
                            <th>Gegner</th>
                            <th></th>
                            <th>Ergebnis</th>
                            <th>Aktion</th>
                        </tr>
                    </thead>
                    <tbody>
                        {matches.length === 0 ? (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center' }}>
                                    Keine Spiele in diesem Zeitraum.
                                </td>
                            </tr>
                        ) : (
                            matches.map((match) => {
                                const dateObj = new Date(match.date);
                                const formattedDate = dateObj.toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                });

                                const timeObj = new Date(`1970-01-01T${match.time}`);
                                const formattedTime = timeObj.toLocaleTimeString('de-DE', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                });

                                return (
                                    <tr key={match.id}>
                                        <td>{match.tus_team}</td>
                                        <td>{match.is_home ? 'Heim' : 'Gast'}</td>
                                        <td>{formattedDate}</td>
                                        <td>{formattedTime}</td>
                                        <td>{match.opponent}</td>
                                        <td>{match.opponent_team_specification}</td>
                                        <td>
                                            {match.tus_goals != null && match.opponent_goals != null ? (
                                                match.is_home
                                                    ? `${match.tus_goals} : ${match.opponent_goals}`
                                                    : `${match.opponent_goals} : ${match.tus_goals}`
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td className={styles.actionButtons}>
                                            <button
                                                className={styles.greenButton}
                                                onClick={() => setSelectedMatch(match)}
                                            >
                                                Bearbeiten
                                            </button>
                                            <button
                                                className={styles.redButton}
                                                onClick={() => deleteMatch(match)}
                                            >
                                                Löschen
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
}
